import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, FileDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MaskedLine = ({ children, delay, className }) => (
  <span className="-mb-[52px] -ml-[18px] block overflow-hidden pb-[52px] pl-[18px]">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ meta, kpis, onExplore }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 240]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.25]);

  return (
    <header data-testid="hero" className="relative flex min-h-screen flex-col justify-end overflow-hidden bg-graphite">
      <motion.div style={{ y }} className="absolute -top-64 inset-x-0 h-[calc(100%+16rem)]">
        <img
          src={meta.images.hero}
          alt="Villa tropical con piscina infinita en Lombok"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-graphite via-graphite/50 to-graphite/20" />
        <div className="absolute inset-0 bg-jade/25 mix-blend-multiply" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 px-6 pt-40 sm:px-12 lg:px-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
        >
          <img src="/assets/logo-gold.png" alt="Jade Residence" className="h-20 w-auto drop-shadow-[0_2px_10px_rgba(0,0,0,0.45)]" data-testid="hero-logo" />
        </motion.div>
        <MaskedLine delay={0.1} className="mt-6 text-[11px] font-semibold uppercase tracking-[0.35em] text-gold">
          Dossier de Inversión — {meta.location}
        </MaskedLine>
        <h1 className="mt-6 font-serif font-light leading-[0.95] tracking-tight text-ivory">
          <MaskedLine delay={0.28} className="text-6xl sm:text-8xl lg:text-[9rem]">
            Jade
          </MaskedLine>
          <MaskedLine delay={0.42} className="text-6xl italic text-gold sm:text-8xl lg:text-[9rem]">
            Residence
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.6} className="mt-7 max-w-xl text-sm leading-relaxed text-ivory/80 sm:text-base">
          {meta.title} · Apartamentos de inversión en Kuta, Lombok, desde {meta.price}.
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            data-testid="pdf-download-btn"
            href={`${API}/dossier/pdf`}
            className="group inline-flex items-center gap-2 bg-gold px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-graphite transition-colors duration-300 hover:bg-ivory"
          >
            <FileDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            Descargar PDF
          </a>
          <button
            data-testid="hero-explore-btn"
            onClick={onExplore}
            className="group inline-flex items-center gap-2 border border-ivory/40 px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            Explorar dossier
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.1 }}
        className="relative z-10 mt-14 border-t border-ivory/15 bg-graphite/40 backdrop-blur-md"
      >
        <div className="grid grid-cols-2 divide-x divide-ivory/10 lg:grid-cols-4">
          {kpis.map((k, i) => (
            <div key={k.label} data-testid={`hero-kpi-${i}`} className="px-6 py-6 lg:px-10">
              <p className="font-serif text-3xl text-gold lg:text-4xl">{k.value}</p>
              <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-ivory/60">{k.label}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </header>
  );
};
